import { UserManagerSettings, WebStorageStateStore } from 'oidc-client';
import { Log, UserManager, User } from 'oidc-client';
import Router, { NavigationGuard } from 'vue-router';
import { CreateElement } from 'vue';
import {
    SignInType,
    AuthServiceContract
} from '@/core/contracts/authServiceContract';
import { inject, injectable } from 'inversify';
import { ConfigServiceContract, CONTRACTS } from '../contracts';

/**
 * Provides access to authenticated user,
 * methods for creating signin components
 * and oidc manager.
 * @export
 * @class AuthService
 */
@injectable()
export class AuthService implements AuthServiceContract {
    private _manager!: UserManager;
    private _user: User | null = null;
    private _initialized: boolean = false;
    private _callback?: () => Promise<any> = undefined;

    constructor(
        @inject(CONTRACTS.ConfigService)
        private _configService: ConfigServiceContract
    ) {
        Log.level = Log.INFO;
    }

    public init(): void {
        this.ensureManagerIsCreated();
    }

    /**
     * User is authenticated.
     * @readonly
     * @type {boolean}
     * @memberof AuthService
     */
    public get isAuthenticated(): boolean {
        return !!this._user && !this._user.expired;
    }

    /**
     * Session access token.
     * @readonly
     * @type {(string | null)}
     * @memberof AuthService
     */
    public get accessToken(): string | null {
        return !!this._user && !this._user.expired
            ? this._user.access_token
            : null;
    }

    /**
     * Authenticated user data.
     * @readonly
     * @type {any}
     * @memberof AuthService
     */
    public get userProfile(): {} {
        return !!this._user && !this._user.expired ? this._user.profile : {};
    }

    /**
     * Service startup.
     * @returns {Promise<void>}
     * @memberof AuthService
     */
    public startupAsync(callback?: () => Promise<any>): Promise<any> {
        this._callback = callback;

        if (this._initialized) {
            return this.callback();
        } else {
            return this._manager.getUser().then(user => {
                if (user && !user.expired) {
                    this._user = user;
                    this._initialized = true;
                    return this.callback();
                } else {
                    return this._manager.removeUser();
                }
            });
        }
    }

    /**
     * Create navigation guard for vue-router.
     * @returns {NavigationGuard}
     * @memberof AuthService
     */
    public createNavigationGuard(): NavigationGuard {
        const guard: NavigationGuard = (to, from, next) => {
            if (to.matched.some(record => record.meta.auth === true)) {
                if (this.isAuthenticated) {
                    next();
                } else {
                    this.signIn(SignInType.Default, { state: { to } })
                        .then(() => next(false))
                        .catch(() => next(false));
                }
            } else {
                next();
            }
        };
        return guard;
    }

    /**
     * Create vue component for signin operations.
     * @param {Router} router
     * @param {SignInType} type
     * @returns {Component}
     * @memberof AuthService
     */
    public createSignInRouteComponent(
        router: Router,
        type: SignInType
    ): Component {
        return {
            render: (h: CreateElement) => h('div'),
            created: async () => {
                try {
                    const data = await this.handleSignin(type);
                    if (type === SignInType.Default) {
                        const redirect = data.state ? data.state.to : null;
                        await this.callback();
                        router.replace(redirect || '/');
                    }
                } catch {
                    if (type === SignInType.Default) {
                        router.replace('/');
                    }
                }
            }
        };
    }

    /**
     * Signin on auth server.
     * @param {SignInType} type
     * @param {*} [args]
     * @returns {Promise<any>}
     * @memberof AuthService
     */
    public signIn(type: SignInType, args?: any): Promise<any> {
        if (args === undefined) {
            args = { state: { to: process.env.BASE_URL } };
        }

        switch (type) {
            case SignInType.Default:
                return this._manager.signinRedirect(args);
            case SignInType.Silent:
                return this._manager.signinSilent(args);
        }
        throw new Error('Unknown signin type.');
    }

    /**
     * Signout from auth server.
     * @param {SignInType} type
     * @param {*} [args]
     * @returns {Promise<any>}
     * @memberof AuthService
     */
    public signOut(type: SignInType, args?: any): Promise<any> {
        switch (type) {
            case SignInType.Default:
                return this._manager.signoutRedirect(args);
        }
        throw new Error('Unknown signin type.');
    }

    /**
     * Handle signin operation bases on its type.
     * @private
     * @param {SignInType} type
     * @param {string} [url]
     * @returns {Promise<any>}
     * @memberof AuthService
     */
    private handleSignin(type: SignInType, url?: string): Promise<any> {
        switch (type) {
            case SignInType.Default:
                return this._manager.signinRedirectCallback(url);
            case SignInType.Silent:
                return this._manager.signinSilentCallback(url);
        }
        throw new Error('Unknown signin type.');
    }

    /**
     * Create and setup oidc-client UserManager.
     * @private
     * @returns {UserManager}
     * @memberof AuthService
     */
    private createUserManager(settings: UserManagerSettings): UserManager {
        const manager = new UserManager(settings);

        manager.events.addAccessTokenExpiring(() => {
            Log.debug('token expiring');
        });

        manager.events.addAccessTokenExpired(() => {
            Log.debug('token expired');
            this.signOut(SignInType.Default);
        });

        manager.events.addSilentRenewError(event => {
            Log.error('silent renew error', event.message);
            if (this.isAuthenticated) {
                setTimeout(() => {
                    this._manager.signinSilent();
                }, 5000);
            }
        });

        manager.events.addUserLoaded(user => {
            this._user = user;
        });

        manager.events.addUserUnloaded(() => {
            this._user = null;
        });

        manager.events.addUserSignedOut(() => {
            this._user = null;
        });

        return manager;
    }

    /**
     * Run callback.
     * @private
     * @returns {Promise<any>}
     * @memberof AuthService
     */
    private async callback(): Promise<any> {
        if (this._callback) {
            await this._callback();
        }
    }

    private ensureManagerIsCreated() {
        if (!this._manager) {
            const config = this._configService.getConfig();
            this._manager = this.createUserManager({
                ...config.Auth,
                userStore: new WebStorageStateStore({
                    store: window.localStorage
                })
            });
        }
    }
}
